// App.tsx - BAM Database Layer Integration Reference
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppLayout from './components/layout/AppLayout';
import HomePage from './components/HomePage';
import DataQualityHub from './components/DataQualityHub';
import CategoriesQualityHub from './components/categories/CategoriesQualityHub';
import ProgramCardPrint from './components/programs/ProgramCardPrint';
import ProgramCardV2 from './components/programs/ProgramCardV2';
import { ProgramReportCard } from './features/quality-check/report-card';
import { QualityCheckCard } from './features/quality-check/workflow';
import CategoryCardPrint from './components/categories/CategoryCardPrint';
import PrintResultsPage from './components/reports/PrintResultsPage';
import CategoryPrintResultsPage from './components/categories/CategoryPrintResultsPage';
import ReportsRoute from './features/reports/ReportsRoute';
import { QualityCheckRoute } from './features/quality-check';
import { PerfectMatchRoute } from './features/perfect-match';
import { CategoryExplorerRoute } from './features/category-explorer';
import { BamDatabaseRoute } from './features/bam-database';
import { SearchDiscoverRoute } from './features/search-discover';
import type { LayoutMode } from './types/components';
import { useHashRoute } from './hooks/useHashRoute';
import './index.css';

// Create a client for React Query with optimized cache settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 2 * 60 * 1000, // 2 minutes default
      gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      placeholderData: (previousData: unknown) => previousData,
    },
    mutations: {
      retry: 1,
      retryDelay: 500,
    },
  },
});

function App(): React.JSX.Element {
  const { view: currentView, setView } = useHashRoute('data-quality');

  // Redirect to data-quality with default params if landing on homepage
  React.useEffect(() => {
    if (currentView === 'homepage' || (!window.location.hash || window.location.hash === '#')) {
      window.location.hash = '#data-quality?sb=categories&sd=desc';
    }
  }, [currentView]);

  const renderCurrentView = (): React.ReactNode => {
    // Get URL params
    const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const programCode = urlParams.get('program_code');
    const programId = urlParams.get('program_id');

    switch (currentView) {
      case 'homepage':
        return <HomePage onNavigate={setView} />;
      case 'program-card':
        return <ProgramCardPrint />;
      case 'program-card-v2':
        // New Report Card component (replaces old ProgramCardV2)
        return programCode ? <ProgramReportCard baciCode={programCode} /> : <ProgramCardV2 />;
      case 'category-card':
        return <CategoryCardPrint />;
      case 'print-results':
        return <PrintResultsPage />;
      case 'print-cat-results':
        return <CategoryPrintResultsPage />;
      case 'categories-quality':
        return <CategoriesQualityHub />;
      case 'bam-database':
        return <BamDatabaseRoute />;
      case 'reports':
        return <ReportsRoute />;
      case 'quality-check':
        return <QualityCheckRoute />;
      case 'quality-checkv2':
        // New QC Workflow component
        return programId && programCode ? (
          <QualityCheckCard programId={programId} baciCode={programCode} />
        ) : (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <h2>Missing program_id or program_code parameter</h2>
            <a href="#data-quality">← Back to Data Quality</a>
          </div>
        );
      case 'operations':
        return <QualityCheckRoute initialTab="operations" />;
      case 'manual-entry':
        return <QualityCheckRoute initialTab="manual" />;
      case 'enrichment-verification':
        return <QualityCheckRoute initialTab="verification" />;
      case 'perfect-match':
        return <PerfectMatchRoute />;
      case 'match':
        return <PerfectMatchRoute />;
      case 'category-explorer':
        return <CategoryExplorerRoute />;
      case 'search-discover':
        return <SearchDiscoverRoute />;
      case 'data-quality':
        return <DataQualityHub />;
      default:
        return <HomePage onNavigate={setView} />;
    }
  };

  const layoutMode: LayoutMode = 'admin';

  // Print routes, card routes, and homepage should render without AppLayout wrapper
  const isStandaloneRoute = ['homepage', 'print-results', 'print-cat-results', 'program-card', 'program-card-v2', 'category-card', 'quality-checkv2', 'category-explorer'].includes(currentView);

  if (isStandaloneRoute) {
    return (
      <QueryClientProvider client={queryClient}>
        {renderCurrentView()}
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AppLayout
        currentView={currentView}
        onViewChange={setView}
        layoutMode={layoutMode}
      >
        <main>
          {renderCurrentView()}
        </main>
      </AppLayout>
    </QueryClientProvider>
  );
}

export default App;
